import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type FormEvent,
} from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import {
	COUNTRY_OPTIONS,
	US_STATE_OPTIONS,
} from '#app/routes/mocra+/_form+/components/addresses/schema.ts'
import {
	createAddressDraft,
	sanitizeAddressDraft,
	type AddressDraft,
} from '#app/routes/mocra+/_form+/components/addresses/types.ts'

type Mode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof AddressDraft, string>>

type Props = {
	readonly mode: Mode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: AddressDraft) => void
	readonly initialValue?: AddressDraft
	readonly title?: string
	readonly description?: string
	readonly submitLabel?: string
	readonly cancelLabel?: string
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

const DEFAULT_COPY = {
	title: {
		add: 'Add address',
		edit: 'Edit address',
	},
	submit: {
		add: 'Add',
		edit: 'Save',
	},
	description:
		'Enter the address details exactly as they appear on registrations and regulatory correspondence.',
}

export function AddressOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	title,
	description,
	submitLabel,
	cancelLabel = 'Cancel',
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<AddressDraft>(
		initialValue ? { ...initialValue } : createAddressDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})
	const wasOpenRef = useRef(open)

	useEffect(() => {
		if (open) {
			setDraft(initialValue ? { ...initialValue } : createAddressDraft())
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open && wasOpenRef.current && typeof onClosed === 'function') {
			onClosed()
		}
		wasOpenRef.current = open
	}, [open, onClosed])

	const descriptionCopy = description ?? DEFAULT_COPY.description
	const titleCopy =
		title ?? (mode === 'add' ? DEFAULT_COPY.title.add : DEFAULT_COPY.title.edit)
	const submitCopy =
		submitLabel ??
		(mode === 'add' ? DEFAULT_COPY.submit.add : DEFAULT_COPY.submit.edit)

	const countryOptions = useMemo(() => COUNTRY_OPTIONS, [])
	const stateOptions = useMemo(() => US_STATE_OPTIONS, [])
	const isUnitedStates = draft.country === 'US'

	const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
		const nextCountry = event.target.value
		setDraft((prev) => ({
			...prev,
			country: nextCountry,
			state:
				nextCountry === 'US'
					? prev.state &&
						stateOptions.some((option) => option.value === prev.state)
						? prev.state
						: (stateOptions[0]?.value ?? '')
					: '',
		}))
	}

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		event.stopPropagation()
		const sanitized = sanitizeAddressDraft(draft)
		const validationErrors = validateDraft(sanitized)
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors)
			return
		}
		onSubmit(sanitized)
		onOpenChange(false)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={descriptionCopy}
			className="sm:max-w-xl"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						{cancelLabel}
					</Button>
					<Button type="submit" form="address-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<form id="address-overlay-form" onSubmit={handleSubmit}>
				<Field
					labelProps={{ children: 'Street address' }}
					inputProps={{
						value: draft.street,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, street: event.target.value })),
						type: 'text',
					}}
					errors={errors.street ? [errors.street] : undefined}
				/>
				<Field
					labelProps={{ children: 'Address line 2 (optional)' }}
					inputProps={{
						value: draft.street2 ?? '',
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, street2: event.target.value })),
						type: 'text',
					}}
					descriptionProps={{ children: 'Suite, unit, or floor if needed.' }}
				/>
				<Field
					labelProps={{ children: 'City' }}
					inputProps={{
						value: draft.city,
						onChange: (event: ChangeEvent<HTMLInputElement>) =>
							setDraft((prev) => ({ ...prev, city: event.target.value })),
						type: 'text',
					}}
					errors={errors.city ? [errors.city] : undefined}
				/>
				<div className="grid gap-4 sm:grid-cols-2">
					{isUnitedStates ? (
						<SelectField
							labelProps={{ children: 'State' }}
							selectProps={{
								value: draft.state,
								onChange: (event: ChangeEvent<HTMLSelectElement>) =>
									setDraft((prev) => ({ ...prev, state: event.target.value })),
							}}
							options={stateOptions}
							errors={errors.state ? [errors.state] : undefined}
						/>
					) : (
						<Field
							labelProps={{ children: 'State / province / region' }}
							inputProps={{
								value: draft.state,
								onChange: (event: ChangeEvent<HTMLInputElement>) =>
									setDraft((prev) => ({ ...prev, state: event.target.value })),
								type: 'text',
							}}
							errors={errors.state ? [errors.state] : undefined}
						/>
					)}
					<Field
						labelProps={{ children: 'ZIP or postal code' }}
						inputProps={{
							value: draft.postalCode,
							onChange: (event: ChangeEvent<HTMLInputElement>) =>
								setDraft((prev) => ({
									...prev,
									postalCode: event.target.value,
								})),
							type: 'text',
						}}
						errors={errors.postalCode ? [errors.postalCode] : undefined}
					/>
				</div>
				<SelectField
					labelProps={{ children: 'Country' }}
					selectProps={{
						value: draft.country,
						onChange: handleCountryChange,
						className: 'overflow-y-auto max-h-[5rem]',
					}}
					options={countryOptions}
					errors={errors.country ? [errors.country] : undefined}
				/>
			</form>
		</ResponsiveOverlay>
	)
}

function validateDraft(draft: AddressDraft): DraftErrors {
	const nextErrors: DraftErrors = {}
	if (!draft.street || draft.street.trim().length === 0) {
		nextErrors.street = 'Enter the street address.'
	}
	if (!draft.city || draft.city.trim().length === 0) {
		nextErrors.city = 'Enter the city.'
	}
	if (!draft.postalCode || draft.postalCode.trim().length === 0) {
		nextErrors.postalCode = 'Enter the postal code.'
	}
	if (!draft.country || draft.country.trim().length === 0) {
		nextErrors.country = 'Select the country.'
	}
	if (draft.country === 'US') {
		const validState = US_STATE_OPTIONS.some(
			(option) => option.value === draft.state,
		)
		if (!validState) {
			nextErrors.state = 'Select the state.'
		}
	} else if (!draft.state || draft.state.trim().length === 0) {
		nextErrors.state = 'Enter the state, province, or region.'
	}
	return nextErrors
}
